import { FEELINGS, totalOf } from 'components/stats/statsFormat';
import {
  anchorOf,
  chartWeeks,
  daysOf,
  isoWeek,
  mondayOf,
  ofMonth,
  periodNames,
  shortDay,
} from 'components/stats/statsPeriods';
import { addDays, dayLabel, fromIsoDay, isoDay } from 'helper/timeLabels';

/**
 * What the statistics page says of a period, as in the prototype: its
 * banner, its chart and « Mon ressenti ». `text` and `plural` are those of
 * useText('stats'); `view` says whose figures they are: `own` (the signed-in
 * commercial's), `single` (one person's) and `firstName`.
 */

// The kinds of period and their slug in the address (?vue=).
export const KINDS = [
  ['semaine', 'week'],
  ['mois', 'month'],
  ['trimestre', 'quarter'],
  ['periode', 'custom'],
];
export const kindOfSlug = (slug) =>
  (KINDS.find(([one]) => one === slug) ?? KINDS[0])[1];
export const slugOfKind = (kind) => KINDS.find(([, one]) => one === kind)[0];

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);
const dayKeys = (period) => daysOf(period).map(isoDay);

export const periodTotal = (byDay, period) => totalOf(byDay, dayKeys(period));
export const weekTotal = (byDay, monday) =>
  periodTotal(byDay, { start: monday, end: addDays(monday, 7) });

// The goal of the period, from the weekly one: 52 weeks make 12 months.
export const goalOf = (kind, period, weekGoal) => {
  if (kind === 'week') return weekGoal;
  if (kind === 'custom')
    return Math.max(1, Math.round((weekGoal * period.days) / 7));
  return Math.round((weekGoal * 52) / 12) * (kind === 'month' ? 1 : 3);
};

// The days the page reads: the period, the one before, the bars of the chart
// and, for a week, six months of history (« best week since… »).
export const rangeOf = (kind, period, previous) => {
  if (kind === 'custom') return { from: previous.start, to: period.end };
  const from = [
    previous.start,
    chartWeeks(period)[0],
    kind === 'week' ? addDays(period.start, -7 * 26) : period.start,
  ].reduce((earliest, day) => (day < earliest ? day : earliest));
  return { from, to: period.end };
};

/**
 * The banner of the period: { title, text, link, pill }. `has(monday)`: the
 * person viewed reviewed that week; `go(kind, day)` and `openReview(monday)`
 * are the page's.
 */
export const bannerOf = ({
  kind,
  period,
  previous,
  current,
  goal,
  weekGoal,
  byDay,
  from,
  today,
  has,
  view,
  text,
  plural,
  go,
  openReview,
}) => {
  const names = periodNames(period, text);
  const n = current.rdvTaken;
  const left = Math.max(0, goal - n);
  const ongoing = period.start <= today && today < period.end;
  if (kind === 'custom') {
    let title = text('bannerCustomOver');
    if (ongoing) title = text('bannerCustomOngoing');
    if (n >= goal) title = text('bannerCustomReached');
    return {
      title,
      text: plural('bannerCustomText', period.days, { n, goal, weekGoal }),
    };
  }

  const elapsed = period.weeks.filter((monday) => monday <= today);
  const done = elapsed.filter(has).length;
  if (kind === 'week') {
    const reviewed = has(period.start);
    let link = null;
    if (view.own)
      link = {
        label: text(reviewed ? 'bannerReviewAgain' : 'bannerReviewDo'),
        onClick: () => openReview(period.start),
      };
    else if (view.single && reviewed)
      link = {
        label: text('bannerReviewSee'),
        onClick: () => openReview(period.start),
      };
    let streak = 0;
    if (view.single)
      for (
        let monday = reviewed ? period.start : addDays(period.start, -7);
        has(monday);
        monday = addDays(monday, -7)
      )
        streak += 1;
    let pill = null;
    if (streak === 1) pill = text('pillStreakOne');
    if (streak > 1) pill = text('pillStreak', { count: streak });
    const hi =
      view.own && view.firstName ? text('hi', { name: view.firstName }) : '';
    if (n >= goal) {
      // Best week since…: the weeks before, as long as they had calls and
      // fewer appointments.
      let since = 0;
      for (
        let monday = addDays(period.start, -7);
        monday >= from;
        monday = addDays(monday, -7)
      ) {
        const week = weekTotal(byDay, monday);
        if (!week.calls || week.rdvTaken >= n) break;
        since += 1;
      }
      const whose = view.own ? 'Own' : 'Other';
      let best = '';
      if (since >= 3) best = text(`bannerBestWeeks${whose}`, { count: since });
      if (since >= 8)
        best = text(`bannerBestMonths${whose}`, {
          count: Math.round(since / 4.35),
        });
      return {
        title: view.own
          ? text('bannerWeekReached', { hi })
          : text('bannerWeekReachedOther'),
        text: `${text('bannerWeekReachedText', { n, goal })}${best}`,
        link,
        pill,
      };
    }
    if (ongoing)
      return {
        title: view.own
          ? text('bannerWeekOngoing', { hi })
          : text('bannerWeekOngoingOther'),
        text: text('bannerWeekOngoingText', { n, goal, left }),
        link,
        pill,
      };
    return {
      title: text('bannerWeekOver', { period: names.short }),
      text: text('bannerWeekOverText', { n, goal }),
      link,
      pill,
    };
  }

  let pill = null;
  if (view.single && elapsed.length)
    pill =
      kind === 'month'
        ? plural(ongoing ? 'pillMonthOngoing' : 'pillMonthOver', done, {
            total: elapsed.length,
            month: names.short,
          })
        : plural('pillQuarter', done, { total: elapsed.length });
  if (kind === 'month') {
    const month = names.short;
    const link = {
      label: text('bannerMonthLink'),
      onClick: () => go('week', ongoing ? today : addDays(period.end, -7)),
    };
    if (n >= goal)
      return {
        title: text('bannerMonthReached'),
        text: text('bannerMonthText', { n, goal, month }),
        link,
        pill,
      };
    if (ongoing) {
      let title = text('bannerMonthEnd');
      if (today.getDate() <= 20) title = text('bannerMonthMiddle');
      if (today.getDate() <= 10) title = text('bannerMonthStart');
      return {
        title,
        text: text('bannerMonthOngoingText', { n, goal, month, left }),
        link,
        pill,
      };
    }
    return {
      title: text('bannerMonthOver', { month: capitalize(month) }),
      text: text('bannerMonthText', { n, goal, month }),
      link,
      pill,
    };
  }

  const pct = Math.round((n / goal) * 100);
  const link = {
    label: text('bannerQuarterLink', {
      period: periodNames(previous, text).short,
    }),
    onClick: () => go('quarter', anchorOf(previous)),
  };
  let title = text('bannerQuarterOver', { period: names.short });
  if (ongoing) title = text('bannerQuarterOngoing');
  if (pct >= 80) title = text('bannerQuarterSolid');
  if (n >= goal) title = text('bannerQuarterReached');
  return {
    title,
    text: text('bannerQuarterText', { n, goal, pct, period: names.short }),
    link,
    pill,
  };
};

/**
 * The chart of the period: day by day for a week (the weekend when it had
 * activity) or a short free period, else week by week. The current day or
 * week when it is in it, else the last one, leads in orange.
 */
export const chartOf = ({ kind, period, byDay, metric, today, text }) => {
  const valueOf = (day) => byDay.get(isoDay(day))?.[metric] ?? 0;
  const sum = (start, span) =>
    daysOf({ start, end: addDays(start, span) }).reduce(
      (total, day) => (day > today ? total : total + valueOf(day)),
      0,
    );
  const custom = kind === 'custom';
  let per = 'Week';
  let bars;
  if (kind === 'week' || (custom && period.days <= 14)) {
    per = 'Day';
    bars = daysOf(period)
      .filter((day) => custom || day.getDay() % 6 !== 0 || valueOf(day) > 0)
      .map((day) => ({
        key: isoDay(day),
        label: custom
          ? shortDay(day)
          : dayLabel(day).split(' ').slice(0, 2).join(' '),
        value: day > today ? null : valueOf(day),
        at: day,
        span: 1,
      }));
  } else if (custom) {
    bars = Array.from({ length: Math.ceil(period.days / 7) }, (_, index) => {
      const start = addDays(period.start, 7 * index);
      const span = Math.min(7, period.days - 7 * index);
      return {
        key: isoDay(start),
        label: shortDay(start),
        value: start > today ? null : sum(start, span),
        at: start,
        span,
      };
    });
  } else {
    bars = chartWeeks(period).map((monday, index) => ({
      key: isoDay(monday),
      label: text('weekBar', { n: index + 1 }),
      value: monday > today ? null : sum(monday, 7),
      at: monday,
      span: 7,
    }));
  }
  const lead =
    bars.find((bar) => bar.at <= today && today < addDays(bar.at, bar.span)) ??
    [...bars].reverse().find((bar) => bar.value != null);
  const suffix = custom ? '' : ` · ${periodNames(period, text).short}`;
  return {
    title: text(`chartTitle${per}`, {
      metric: text(`metricTitle_${metric}`),
      suffix,
    }),
    bars: bars.map((bar) => ({ ...bar, current: bar === lead })),
  };
};

const averageOf = (reviews) =>
  Object.fromEntries(
    FEELINGS.map((key) => [
      key,
      Math.round(
        reviews.reduce((total, review) => total + review[key], 0) /
          reviews.length,
      ),
    ]),
  );

/**
 * « Mon ressenti »: the last review up to this week (one person's week), or
 * the average of the reviews of the period; its victory and its challenge
 * for one person only. { feeling, aside, empty }
 */
export const feelingOf = ({ kind, period, reviews, view, text }) => {
  const names = periodNames(period, text);
  if (kind === 'week') {
    if (view.single) {
      const review = [...reviews]
        .reverse()
        .find((item) => item.weekStart <= isoDay(period.start));
      return {
        feeling: review ?? null,
        aside: review
          ? text('feelingAsideWeek', {
              n: isoWeek(fromIsoDay(review.weekStart)),
            })
          : null,
        empty: text(view.own ? 'feelingEmptyOwn' : 'feelingEmptyOther'),
      };
    }
    const week = reviews.filter(
      (item) => item.weekStart === isoDay(period.start),
    );
    return {
      feeling: week.length ? averageOf(week) : null,
      aside: text('feelingAsideWeekAverage', { period: names.short }),
      empty: text('feelingEmptyWeek', { period: names.short }),
    };
  }
  const weeks = new Set(period.weeks.map(isoDay));
  const inPeriod = reviews.filter((item) => weeks.has(item.weekStart));
  let aside = text('feelingAsideQuarter', { period: names.short });
  let empty = text('feelingEmptyQuarter', { period: names.short });
  if (kind === 'month') {
    aside = text('feelingAsideMonth', { month: ofMonth(period.first, text) });
    empty = text('feelingEmptyMonth', { month: names.short });
  }
  if (kind === 'custom') {
    aside = text('feelingAsideCustom');
    empty = text('feelingEmptyCustom');
  }
  if (!inPeriod.length) return { feeling: null, aside, empty };
  const last = inPeriod[inPeriod.length - 1];
  return {
    feeling: {
      ...averageOf(inPeriod),
      ...(view.single
        ? { victory: last.victory, challenge: last.challenge }
        : {}),
    },
    aside,
    empty,
  };
};

// « C’est vendredi… »: from Friday to Sunday, a commercial who has not
// reviewed this week yet.
export const remindsReview = ({ kind, period, today, has, view }) => {
  const thisWeek = mondayOf(today);
  return (
    view.own &&
    kind === 'week' &&
    isoDay(period.start) === isoDay(thisWeek) &&
    [5, 6, 0].includes(today.getDay()) &&
    !has(thisWeek)
  );
};
